import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Star, ArrowRight, Globe, Shield, Heart, Clock, Users, Phone, Award, Sparkles, ChevronDown, Play } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EnrollPackages = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('IN');
  const [currency, setCurrency] = useState('INR');
  const [openFaq, setOpenFaq] = useState(null);

  // Default packages if API fails
  const defaultPackages = [
    {
      id: 'monthly',
      badge: 'FLEXIBLE ENTRY',
      name: 'Monthly',
      price_display: 'Rs. 17,999',
      price_suffix: '/month',
      button_label: 'START TRIAL',
      featured: false,
      features: [
        'Daily 5-Min Wellness Call',
        'Daily Insight Reports',
        'Direct Principal Access',
      ]
    },
    {
      id: 'quarterly',
      badge: 'TRANSFORMATIONAL',
      name: 'Quarterly',
      price_display: 'Rs 49,999',
      price_suffix: '/3 month',
      button_label: 'CHOOSE GROWTH',
      featured: true,
      features: [
        'All Monthly Features',
        'Personalized Growth Roadmap',
        'Quarterly Goal-Setting Session',
      ]
    }
  ];

  const faqs = [
    {
      q: "How does Sanjaya work?",
      a: "Sanjaya connects your child with a trained observer for gentle 10-15 minute daily conversations. Our AI analyzes patterns and provides you with insights about your child's emotional wellbeing."
    },
    {
      q: "Who are the observers?",
      a: "Our observers are carefully selected and trained professionals with backgrounds in child psychology, education, or counseling. Each observer is verified and supervised by certified principals."
    },
    {
      q: "Is my child's information safe?",
      a: "Absolutely. We use bank-level encryption, never share personal data, and all conversations are confidential. Parents receive only summarized insights, not raw conversation data."
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes! You can cancel your subscription anytime. If you cancel within the first 7 days, you'll receive a full refund. No questions asked."
    },
    {
      q: "What age groups do you serve?",
      a: "Sanjaya is designed for children aged 5-14 years. Our conversation techniques are adapted based on each child's age and developmental stage."
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      role: "Mother of 8-year-old",
      text: "Sanjaya helped us understand our son's anxiety about school. The daily check-ins made such a difference!",
      rating: 5
    },
    {
      name: "Rajesh Patel",
      role: "Father of 10-year-old",
      text: "The weekly reports are incredibly insightful. We finally understand what's going on in our daughter's mind.",
      rating: 5
    },
    {
      name: "Meera Reddy",
      role: "Mother of 6-year-old",
      text: "The observer became like a trusted friend for my child. She looks forward to her daily conversations!",
      rating: 5
    }
  ];

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const savedCountry = localStorage.getItem('user_country');
        if (savedCountry) {
          setCountry(savedCountry);
          setCurrency(savedCountry === 'IN' ? 'INR' : 'USD');
        }
      } catch (e) {
        console.log('Using default country');
      }
    };
    detectCountry();
    loadPackages();
  }, [country]);

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/packages?country=${country}`);
      const apiPkgs = response.data.packages;
      // Only use API packages if they carry the new shape (badge field); otherwise use defaults
      if (apiPkgs && apiPkgs.length > 0 && apiPkgs[0].badge) {
        setPackages(apiPkgs);
        setCurrency(response.data.currency);
      } else {
        setPackages(defaultPackages);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      setPackages(defaultPackages);
    } finally {
      setLoading(false);
    }
  };

  const toggleCountry = () => {
    const newCountry = country === 'IN' ? 'US' : 'IN';
    setCountry(newCountry);
    localStorage.setItem('user_country', newCountry);
    setLoading(true);
    loadPackages();
  };

  const selectPackage = (pkg) => {
    localStorage.setItem('selected_package', JSON.stringify(pkg));
    navigate('/enroll/register');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-orange-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-100/50 to-rose-100/50"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6 shadow-sm border border-orange-100">
            <Sparkles className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-gray-700">Trusted by 500+ families across India</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Give Your Child the Gift of
            <span className="text-orange-600"> Emotional Wellness</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Daily conversations that help your child express feelings, build confidence, and thrive emotionally
          </p>
          
          {/* Country Toggle */}
          <button
            onClick={toggleCountry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-600 hover:bg-gray-50 transition-all shadow-sm"
          >
            <Globe className="w-4 h-4" />
            {country === 'IN' ? '🇮🇳 India (INR)' : '🌍 International (USD)'}
          </button>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-8 px-4 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, text: "100% Safe & Private", color: "orange" },
              { icon: Users, text: "Trained Observers", color: "rose" },
              { icon: Clock, text: "Flexible Scheduling", color: "pink" },
              { icon: Heart, text: "Cancel Anytime", color: "orange" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 justify-center">
                <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                <span className="text-sm font-medium text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 px-4" id="packages">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Choose Your Plan</h2>
            <p className="text-gray-600">Simple, transparent pricing for every family</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`rounded-xl p-8 flex flex-col transition-all duration-300 hover:shadow-2xl ${
                  pkg.featured
                    ? 'bg-gradient-to-br from-orange-500 via-rose-500 to-pink-500 text-white shadow-xl'
                    : 'bg-white border border-orange-100 text-gray-900 shadow-sm hover:border-orange-300 hover:shadow-lg'
                }`}
              >
                {/* Badge */}
                <p className={`text-xs font-semibold tracking-widest uppercase mb-3 ${
                  pkg.featured ? 'text-orange-100' : 'text-orange-400'
                }`}>
                  {pkg.badge}
                </p>

                {/* Plan Name */}
                <h3 className={`text-4xl font-bold mb-4 ${pkg.featured ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.name}
                </h3>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-8">
                  <span className={`text-3xl font-bold ${pkg.featured ? 'text-white' : 'text-orange-600'}`}>
                    {pkg.price_display}
                  </span>
                  <span className={`text-base font-normal ml-1 ${pkg.featured ? 'text-orange-100' : 'text-gray-500'}`}>
                    {pkg.price_suffix}
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-10 flex-1">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      {pkg.featured ? (
                        <span className="w-5 h-5 rounded-full border-2 border-white/70 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </span>
                      ) : (
                        <span className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </span>
                      )}
                      <span className={`text-sm font-medium ${pkg.featured ? 'text-white/90' : 'text-gray-700'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  onClick={() => selectPackage(pkg)}
                  className={`w-full py-5 text-base font-semibold rounded-xl transition-all duration-200 ${
                    pkg.featured
                      ? 'bg-white text-orange-600 hover:bg-orange-50 shadow-lg hover:shadow-xl'
                      : 'bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  {pkg.button_label}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">How Sanjaya Works</h2>
            <p className="text-gray-600">Simple steps to emotional wellness</p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: 1, title: "Sign Up", desc: "Create your account and tell us about your child", icon: "📝" },
              { step: 2, title: "Get Matched", desc: "We pair your child with a trained observer", icon: "🤝" },
              { step: 3, title: "Daily Chats", desc: "Your child has 10-15 min conversations", icon: "💬" },
              { step: 4, title: "Get Insights", desc: "Receive weekly reports and recommendations", icon: "📊" }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl">
                  {item.icon}
                </div>
                <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-sm font-bold">
                  {item.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-gradient-to-br from-orange-50 to-rose-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What Parents Say</h2>
            <p className="text-gray-600">Real stories from real families</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, idx) => (
              <Card key={idx} className="bg-white/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{t.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="text-gray-500 text-sm">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Why Parents Trust Sanjaya</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-bold mb-2">100% Safe & Private</h3>
              <p className="text-gray-600 text-sm">Bank-level encryption. All conversations confidential.</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-rose-600" />
              </div>
              <h3 className="font-bold mb-2">Expert Supervision</h3>
              <p className="text-gray-600 text-sm">Every session reviewed by certified principals.</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-pink-600" />
              </div>
              <h3 className="font-bold mb-2">Satisfaction Guaranteed</h3>
              <p className="text-gray-600 text-sm">Full refund within first 7 days. No questions asked.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${
                    openFaq === idx ? 'rotate-180' : ''
                  }`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Start Your Child's Emotional Wellness Journey Today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 500+ families who've transformed their children's emotional wellbeing
          </p>
          <Button
            onClick={() => document.getElementById('packages').scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-xl shadow-xl"
          >
            Choose Your Plan
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="mt-4 text-sm opacity-80">
            ✓ Free trial available • ✓ Cancel anytime • ✓ No credit card for trial
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnrollPackages;
